package com.example.hukakmp.viewmodel

import androidx.lifecycle.ViewModel
import com.example.hukakmp.counter.FishCounterEntry
import com.example.hukakmp.counter.FishCounterPersistence
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.json.Json

class FishCounterViewModel : ViewModel() {

    data class UiState(
        val entries: List<FishCounterEntry> = emptyList(),
    ) {
        val totalFish: Int get() = entries.sumOf { it.count }
        val totalReturned: Int get() = entries.sumOf { it.returned }
    }

    private val json = Json { ignoreUnknownKeys = true }
    private val _uiState = MutableStateFlow(UiState(loadPersisted()))
    val uiState: StateFlow<UiState> = _uiState.asStateFlow()

    fun add(name: String, quantity: Int) {
        val cleanName = name.trim()
        if (cleanName.isBlank() || quantity <= 0) return

        val mutable = _uiState.value.entries.toMutableList()
        val index = mutable.indexOfFirst { it.name.equals(cleanName, ignoreCase = true) }

        if (index >= 0) {
            val current = mutable[index]
            mutable[index] = current.copy(count = current.count + quantity)
        } else {
            mutable += FishCounterEntry(name = cleanName, count = quantity)
        }
        update(mutable)
    }

    fun increment(name: String) {
        val current = _uiState.value.entries.firstOrNull { it.name.equals(name, ignoreCase = true) }
        if (current == null) add(name, 1) else setCount(name, current.count + 1)
    }

    fun decrement(name: String) {
        val current = _uiState.value.entries.firstOrNull { it.name.equals(name, ignoreCase = true) } ?: return
        setCount(name, current.count - 1)
    }

    fun setCount(name: String, count: Int) {
        if (count <= 0) {
            remove(name)
            return
        }
        update(
            _uiState.value.entries.map {
                if (it.name.equals(name, ignoreCase = true)) {
                    it.copy(
                        count = count,
                        returned = it.returned.coerceAtMost(count),
                    )
                } else it
            }
        )
    }

    fun setReturned(name: String, returned: Int) {
        update(
            _uiState.value.entries.map {
                if (it.name.equals(name, ignoreCase = true)) {
                    it.copy(returned = returned.coerceIn(0, it.count))
                } else it
            }
        )
    }

    fun remove(name: String) {
        update(_uiState.value.entries.filterNot { it.name.equals(name, ignoreCase = true) })
    }

    fun clear() {
        FishCounterPersistence.clear()
        _uiState.value = UiState()
    }

    private fun update(entries: List<FishCounterEntry>) {
        _uiState.value = UiState(entries)
        runCatching {
            FishCounterPersistence.save(
                json.encodeToString(
                    ListSerializer(FishCounterEntry.serializer()),
                    entries,
                )
            )
        }
    }

    private fun loadPersisted(): List<FishCounterEntry> {
        val raw = FishCounterPersistence.load() ?: return emptyList()
        return runCatching {
            json.decodeFromString(
                ListSerializer(FishCounterEntry.serializer()),
                raw,
            )
        }.getOrDefault(emptyList())
    }
}
